import { useEffect, useState, useMemo, useSyncExternalStore } from 'react';
import StepHeader from '@components/step-header/StepHeader';
import { Button, Alert } from '@trussworks/react-uswds';
import DatePicker from '@components/date-picker/DatePicker';
import TextInput from '@components/text-input/TextInput';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useForm, FormProvider } from 'react-hook-form';
import { filterNullEmptyObjects, doClearIncompletePhoneInfo } from '@src/utils/helpers';
import ErrorSummary from '@components/error-summary/ErrorSummary';
import { useNonPcn, useRequest, useUserRoleCapabilities } from '@stores';
import { useResetDirtyFields } from '@hooks';
import { useUserProfile } from '@hooks/useUserProfile';
import { PcnResultTypes, RequestStatus, RequestType } from '@src/utils/enums';
import { getRequestOwner } from '@api/services/authorized-user/helper';
import { AuthorizedUserPermissions } from '@api/services/authorized-user/types';
import { getAppStore } from '@src/utils/store-reference';
import { useNonPcnUpdateMutation } from '@api/services/non-pcn/hooks/useNonPcnUpdateMutation';
import { useFileStorageUploadMutation } from '@api/services/file-storage/hooks/useFileStorageUploadMutation';
import useGetAuthorizedUsers from '@api/services/authorized-user/hooks/useGetAuthorizedUsers';
import { useLookupTableFormVersions } from '@api/services/lookup/hooks';
import CertifySignSubmitRoleAlert from '@forms/input-forms/common/CertifySignSubmitRoleAlert';

export const NonPcnFormCertifySignSubmitMetadata = {
  sectionName: 'Certify, Sign, and Submit',
};

const nonPcnResultMessage = {
  [PcnResultTypes.PcnRequired]: {
    result: 'requires a Pre-construction Notification',
    action: 'request an Application for Department of the Army (DA) Permit',
  },
  [PcnResultTypes.NonPcnLetter]: {
    result: 'does not require a Pre-construction Notification',
    action: 'download your letter',
  },
  [PcnResultTypes.ContactDistrict]: {
    result: 'requires contacting the District',
    action: 'contact the District',
  },
  [PcnResultTypes.PreApplication]: {
    result: `requires a Pre-application Meeting`,
    action: 'request a Pre-application/Project Scoping',
  },
};

const NonPcnFormCertifySignSubmit = ({ stepNo }) => {
  const [isSigned, setIsSigned] = useState(false);

  const store = useMemo(() => getAppStore(), []);

  const requestAPIData = useSyncExternalStore(store.subscribe, () => store.selectRequestAPIData());
  const requestFormData = useSyncExternalStore(store.subscribe, () => store.selectRequestFormData());
  const nonPcnErrors = useSyncExternalStore(store.subscribe, () => store.selectPreAppErrors());
  const requestID = useSyncExternalStore(store.subscribe, () => store.selectRequestID());
  const version = useSyncExternalStore(store.subscribe, () => store.selectVersion());
  const requestStatus = useSyncExternalStore(store.subscribe, () => store.selectRequestStatus());
  const validFiles = useSyncExternalStore(store.subscribe, () => store.selectValidFiles());

  const doUpdateRequestObject = store.doUpdateRequestObject;

  const { data: authorizedUsersData } = useGetAuthorizedUsers({ requestId: requestID, version });
  const { mutateAsync: updateNonPcn } = useNonPcnUpdateMutation();
  const { mutateAsync: uploadRequestFiles } = useFileStorageUploadMutation();

  const { userProfileData } = useUserProfile();
  const canSign = useUserRoleCapabilities((state) => state.roleCapabilities.signSubmitRequest);
  const { nonPcnData } = useNonPcn();
  const lookupTableFormVersions = useLookupTableFormVersions();
  const { isFormReadOnly: isReadOnly, updateSection, isSectionTouched, getSections } = useRequest();
  const sections = getSections();

  const authorizedUsers = authorizedUsersData ?? [];
  const requestOwner = getRequestOwner(authorizedUsers);
  const isRequestOwner = requestAPIData?.request?.permission === AuthorizedUserPermissions.Owner;
  const canUserSign = canSign && isRequestOwner;
  const hasSubmitted = requestStatus !== RequestStatus.Draft;
  const formVersion = lookupTableFormVersions.find(
    (item) => item.requestType === RequestType.NonPreconstructionNotification
  )?.formVersion;

  // Signature always validated against user profile name
  const fullName = useMemo(
    () =>
      userProfileData?.middleName
        ? `${userProfileData.firstName} ${userProfileData.middleName} ${userProfileData.lastName}`
        : `${userProfileData?.firstName} ${userProfileData?.lastName}`,
    [userProfileData?.firstName, userProfileData?.middleName, userProfileData?.lastName]
  );

  const defaultValues = useMemo(
    () => ({
      signature: requestAPIData?.request?.signature ?? null,
      signatureDate:
        requestAPIData?.request?.signatureDate !== null ? requestAPIData?.request?.signatureDate?.split('T')[0] : '',
    }),
    [requestAPIData]
  );

  const isFormValid = useMemo(() => {
    // Check if any OTHER sections (not this one) are invalid
    return !sections.some(
      (section) => section.id !== stepNo && section.valid === false && section.label !== 'Certify, Sign, and Submit'
    );
  }, [sections, stepNo]);

  const schema = useMemo(
    () =>
      yup.object().shape({
        signature: yup.string().test('isSigned', `Signature must be ${fullName}`, (val) => val.trim() === fullName),
        signatureDate: yup.string().required('Please select a date'),
      }),
    [fullName]
  );

  const methods = useForm({ defaultValues: defaultValues, resolver: yupResolver(schema), mode: 'onBlur' });
  const {
    formState: { errors, dirtyFields },
    setValue,
    watch,
    handleSubmit,
    trigger,
    reset,
  } = methods;

  const isDirty = Object.keys(dirtyFields).length > 0;
  const errorCount = Object.values(errors).length;
  const isValid = errorCount === 0 && isFormValid && isSigned;
  const isTouched = isSectionTouched(stepNo);

  const signature = watch('signature');
  const signatureDate = watch('signatureDate');
  const dateNow = new Date().toISOString().slice(0, 10);

  const resultMessage = nonPcnResultMessage[nonPcnData.nonPCNResult];

  // Reset the dirty states of the fields after a save.
  useResetDirtyFields(isTouched, requestAPIData, reset, trigger);

  const handleBlur = (e) => {
    setValue('signature', e.target.value.trim());
  };

  useEffect(() => {
    if (signature?.trim() === fullName) {
      !signatureDate && setValue('signatureDate', new Date().toISOString().slice(0, 10));
      setIsSigned(true);
      trigger();
    } else {
      setIsSigned(false);
    }
  }, [signature, fullName, setValue, trigger, signatureDate]);

  const submitForm = () => {
    //require submission endpoint would be called here
    const requestFormDataSubmit = {
      ...requestFormData,
      request: { ...requestFormData.request, isSubmit: true, formVersion: formVersion },
    };
    const cleanedPayload = doClearIncompletePhoneInfo(requestFormDataSubmit);
    const filteredRequest = filterNullEmptyObjects(cleanedPayload);
    validFiles.length > 0
      ? uploadRequestFiles({ data: filteredRequest, updateFn: updateNonPcn })
      : updateNonPcn(filteredRequest);
  };

  useEffect(() => {
    updateSection(stepNo, { dirty: isDirty, valid: isValid });
  }, [stepNo, isDirty, isValid, updateSection]);

  useEffect(() => {
    if (isValid && signature && signatureDate) {
      doUpdateRequestObject({ signature: signature, signatureDate: signatureDate });
    }
  }, [isValid, signature, signatureDate, doUpdateRequestObject]);

  const renderSignatureSection = () => {
    if (!isFormValid) {
      return (
        <Alert slim className='margin-bottom-3' type='error'>
          In order to submit this form, please revisit the section(s) marked with errors, and correct any missing or
          invalid fields.
        </Alert>
      );
    }

    return (
      <>
        <p className='font-sans-sm text-bold border-bottom-1px border-base-light width-full padding-bottom-2'>
          Signature
        </p>
        <div className='grid-row grid-gap'>
          <div className='grid-col-6'>
            <TextInput
              name='signature'
              label='Signature'
              signature={canUserSign && fullName}
              required
              readOnly={isReadOnly || !canUserSign}
              onBlur={handleBlur}
            />
          </div>
          <div className='grid-col-6'>
            <DatePicker
              min={null}
              max={dateNow}
              name='signatureDate'
              label='Signature Date'
              readOnly={isReadOnly || !canUserSign}
              required
            />
          </div>
        </div>
        <p className='font-sans-sm text-bold border-bottom-1px border-base-light width-full padding-bottom-2'></p>
        <div className='grid-row grid-gap'>
          <div className='grid-col-4 margin-bottom-3 padding-bottom-2'>
            <Button
              className='submit-request-button'
              title='Submit Request'
              disabled={!isSigned || isReadOnly || !canUserSign}
              onClick={handleSubmit(submitForm)}
            >
              Submit Request
            </Button>
          </div>
        </div>
      </>
    );
  };

  return (
    <FormProvider {...methods}>
      {(!isFormValid || errorCount > 0) && !isReadOnly && <ErrorSummary errors={errors} type='form' />}
      {Object.keys(nonPcnErrors)?.length > 0 && <ErrorSummary errors={nonPcnErrors} type='api-validation' />}
      {resultMessage && (
        <Alert heading='RESULT' headingLevel='h5' type='warning' noIcon>
          Based on the answers you have provided to the questionnaire, your project{' '}
          <span className='text-bold'>{resultMessage.result}</span>.
          <div className='margin-top-1' />
          Upon submitting this request, you will be presented with an option to{' '}
          <span className='text-bold'>{resultMessage.action}</span>.
        </Alert>
      )}
      <CertifySignSubmitRoleAlert showAlert={!hasSubmitted && !canSign} />
      {!isRequestOwner && !hasSubmitted && (
        <Alert slim className='margin-top-3 margin-bottom-3' type='warning'>
          You are not authorized to Certify, Sign and Submit this request. Only the{' '}
          <span className='text-bold'>
            Request Owner: {requestOwner?.firstName} {requestOwner?.lastName}
          </span>{' '}
          can perform this operation.
        </Alert>
      )}
      <StepHeader text='Certify, Sign, and Submit'>
        <span className='text-bold'>
          The Application must be signed by the person who desires to undertake the proposed activity (applicant) or it
          may be signed by a duly authorized agent if a completed agent authorization is provided. A Department of the
          Army (DA) permit does not convey any property rights, either in real estate or material, or any exclusive
          privileges. Furthermore, a DA permit does not authorize any injury to property or invasion of rights or any
          infringement of Federal, state or local laws or regulations. The applicant's signature on an application is an
          affirmation that the applicant possesses or will possess the requisite property interest to undertake the
          activity proposed in the application. The district engineer will not enter into disputes but will remind the
          applicant of the above. The dispute over property ownership will not be a factor in the Corps public interest
          decision. (33 FR 320.4(g)(6))
        </span>
      </StepHeader>
      <div className='text-bold'>
        <div className='grid-row margin-left-2'>
          <p className='font-sans-sm text-bold width-full padding-bottom-2'>
            BY SUBMITTING THIS REQUEST, I AM AGREEING TO ALL OF THE FOLLOWING:
          </p>
        </div>
        <ul>
          <li>
            I consent to conduct business electronically and understand this consent can be withdrawn at any time in
            writing to the local USACE Regulatory office.
          </li>
          <li>Application is hereby made for permit or permits to authorize the work described in this application.</li>
          <li>
            I certify that the information in this application is complete and accurate. I understand that the signature
            of the applicant or the agent will be an affirmation that the applicant possesses or will possess the
            requisite property interest to undertake the activity proposed in the application.
          </li>
          <li>
            I hereby certify that I have included all activities that require a USACE permit that I plan to undertake
            and that are reasonably related to the project proposed in this permit application.
          </li>
          <li>
            I understand that, whoever, in any matter within the jurisdiction of any department of agency of the United
            States, knowingly and willfully (1) falsifies, conceals, or covers up by any trick, scheme, or device a
            material fact; (2) makes any materially false, fictitious, or fraudulent statement or representation; or (3)
            makes or uses any false writing or document knowing the same to contain any materially false, fictitious, or
            fraudulent statement or entry; shall be fined not more than $10,000 or imprisoned not more than 5 years or
            both. (18 USC Section 1001)
          </li>
          <li>
            I understand that by typing my name in the text field below that I am e-signing this application and I
            understand that this e-signature has the same legal status as handwritten signatures.
          </li>
        </ul>
      </div>
      {renderSignatureSection()}
    </FormProvider>
  );
};

NonPcnFormCertifySignSubmit.metadata = NonPcnFormCertifySignSubmitMetadata;

export default NonPcnFormCertifySignSubmit;
